import { Link } from 'react-router-dom';
import { ArrowRight, Check, MapPin, Home as HomeIcon, ChevronDown, Sparkles } from 'lucide-react';
import { useState } from 'react';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { LOCAL_BUSINESS_SCHEMA, localServiceSchema, faqSchema } from '../lib/schema';
import { useLang } from '../context/LanguageContext';

const Apeldoorn = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const { ui, lp } = useLang();
  const t = ui.apeldoorn;

  return (
    <div className="bg-[#0a0a0b] text-white">
      <SEO
        path="/detailing-apeldoorn"
        title={t.seoTitle}
        description={t.seoDesc}
        keywords={t.keywords}
        jsonLd={[
          LOCAL_BUSINESS_SCHEMA,
          localServiceSchema({ city: 'Apeldoorn', path: '/detailing-apeldoorn' }),
          faqSchema(t.faq.map(f => ({ q: f.q, a: f.a }))),
        ]}
      />

      {/* HERO */}
      <section className="relative min-h-[82vh] flex items-end pb-20 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=1920&q=85" alt="Shiny-Cleaning autodetailing voor klanten in Apeldoorn" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 w-full">
          <SectionLabel>{t.heroLabel}</SectionLabel>
          <h1 className="mt-6 text-5xl md:text-7xl lg:text-[76px] font-medium leading-[1.05] tracking-tight max-w-4xl">
            {t.heroTitle[0]}<br />{t.heroTitle[1]}
          </h1>
          <p className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed">{t.heroSub}</p>
          <div className="mt-10">
            <BookingCTA size="lg" waMessage={t.wa} offerteTreatment={t.treatment} />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-28 border-t border-white/5">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">{t.introTitle}</h2>
          <p className="mt-5 text-white/65 leading-relaxed">{t.introText}</p>
        </div>
      </section>

      {/* DIENSTEN */}
      <section className="py-24 lg:py-28 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <SectionLabel><span className="mx-auto">{t.dienstenLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.dienstenTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.diensten.map(d => (
              <Link key={d.slug + d.title} to={lp('service', { slug: d.slug })} className="group p-7 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all">
                <h3 className="text-white text-lg font-medium leading-tight">{d.title}</h3>
                <p className="mt-2 text-white/55 text-sm leading-relaxed">{d.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[#7aa3ff] text-sm group-hover:text-white transition-colors">
                  {ui.diensten.moreInfo} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILING AAN HUIS (emphasis) */}
      <section className="py-24 lg:py-28 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <HomeIcon className="w-4 h-4" /> {t.aanhuisLabel}
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.aanhuisTitle}</h2>
            <p className="mt-5 text-white/70 leading-relaxed">{t.aanhuisText}</p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <BookingCTA waMessage={t.wa} offerteTreatment={t.treatment} />
              <Link to={lp('mobile')} className="inline-flex items-center gap-1.5 text-[#7aa3ff] hover:text-white text-sm transition-colors">
                {t.aanhuisCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3]">
            <img src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200&q=80" alt="Detailing aan huis in Apeldoorn door Shiny-Cleaning" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* SERVICEGEBIED */}
      <section className="py-24 lg:py-28 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="p-8 md:p-10 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <MapPin className="w-4 h-4" /> {t.gebiedLabel}
            </div>
            <h2 className="mt-3 text-2xl md:text-3xl font-medium tracking-tight">{t.gebiedTitle}</h2>
            <p className="mt-4 text-white/70 leading-relaxed">{t.gebiedText}</p>
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {t.usps.map(u => (
              <div key={u.title} className="p-6 rounded-xl border border-white/10 bg-white/[0.02]">
                <Sparkles className="w-5 h-5 text-[#2f6bff]" />
                <h3 className="mt-3 text-white font-medium">{u.title}</h3>
                <p className="mt-1.5 text-white/55 text-sm">{u.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="text-white/40">{t.linksLabel}:</span>
            <Link to={lp('mobile')} className="text-[#7aa3ff] hover:text-white transition-colors">{t.aanhuisCta}</Link>
            <Link to={lp('zutphen')} className="text-[#7aa3ff] hover:text-white transition-colors">{t.linkZutphen}</Link>
            <Link to={lp('raalte')} className="text-[#7aa3ff] hover:text-white transition-colors">{t.linkRaalte}</Link>
            <Link to={lp('diensten')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.diensten.label}</Link>
            <Link to={lp('prijzen')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.mobile.linkPricing}</Link>
            <Link to={lp('portfolio')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.mobile.linkProjects}</Link>
            <Link to={lp('contact')} className="text-[#7aa3ff] hover:text-white transition-colors">{ui.contact.label}</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <SectionLabel><span className="mx-auto">{t.faqLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.faqTitle}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className={`rounded-xl border overflow-hidden transition-colors ${isOpen ? 'border-[#2f6bff]/40 bg-[#2f6bff]/5' : 'border-white/10 bg-white/[0.02]'}`}>
                  <button onClick={() => setOpenFaq(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left">
                    <span className="text-white text-base md:text-lg font-medium">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-white/60 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-[#2f6bff]' : ''}`} />
                  </button>
                  {isOpen && <div className="px-6 pb-5 text-white/75 text-sm leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="p-10 md:p-16 rounded-2xl border border-white/10 bg-gradient-to-br from-[#2f6bff]/12 via-white/[0.03] to-transparent text-center">
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl mx-auto">{t.finalTitle}</h2>
            <p className="mt-5 text-white/60 max-w-xl mx-auto">{t.finalText}</p>
            <div className="mt-10 flex justify-center">
              <BookingCTA size="lg" align="center" waMessage={t.wa} offerteTreatment={t.treatment} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Apeldoorn;
